import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart, useAuth, useWishlist } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import MoleculeLoader from '../components/MoleculeLoader';
import { 
  ShoppingCart, 
  Minus, 
  Plus, 
  Shield, 
  CheckCircle2,
  ArrowLeft,
  FileText,
  FlaskConical,
  AlertCircle,
  Bell,
  Mail,
  Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format image URLs (handle local uploads vs external URLs)
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/uploads') || url.startsWith('/api/uploads')) return `${API_URL}${url}`;
  return url;
};

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { token, user } = useAuth();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifySubscribed, setNotifySubscribed] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setAccessDenied(false);
      try {
        // Include auth token for tier-restricted products
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_URL}/api/products/${slug}`, {
          headers,
          
        });
        
        if (response.ok) {
          const data = await response.json();
          setProduct(data);
          setSelectedVariant(0);
          
          // Fetch related products from same category (also with auth)
          const relatedRes = await fetch(`${API_URL}/api/products?category=${data.category}`, {
            headers,
            
          });
          if (relatedRes.ok) {
            const related = await relatedRes.json();
            setRelatedProducts(related.filter(p => p.slug !== slug).slice(0, 4));
          }
        } else if (response.status === 403) {
          // Tier-restricted product - user doesn't have access
          setAccessDenied(true);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [slug, token]);

  const currentVariant = product?.variants?.[selectedVariant];
  const hasVariants = product?.variants && product.variants.length > 1;
  // Availability: a variant is sellable only when flagged in_stock AND has units.
  // Out-of-stock items can only be added when the product opts into pre-orders
  // (admin "Allow Pre-Orders" toggle, off by default).
  const variantInStock = !!(currentVariant?.in_stock && (currentVariant?.stock_quantity ?? 0) > 0);
  const allowPreorder = product?.allow_preorder === true;
  const canPreorder = !variantInStock && allowPreorder;

  const handleAddToCart = (asPreorder = false) => {
    if (product && currentVariant) {
      const effectivePrice = currentVariant.is_on_sale && currentVariant.sale_price
        ? currentVariant.sale_price
        : currentVariant.price;
      addItem({
        product_id: `${product.product_id}_${selectedVariant}`,
        name: product.name,
        price: effectivePrice,
        original_price: currentVariant.is_on_sale ? currentVariant.original_price : undefined,
        is_on_sale: !!currentVariant.is_on_sale,
        // Carry the eligibility flag so the cart can exclude this line from member-tier
        // discounts and discount codes when the admin has marked it ineligible.
        member_discount_eligible: product.member_discount_eligible !== false,
        image_url: product.image_url,
        vial_size: currentVariant.size,
        variant_index: selectedVariant,
        is_preorder: asPreorder,
        stock_quantity: currentVariant.stock_quantity || 0
      }, quantity);
    }
  };

  const handleNotifyMe = async () => {
    if (!notifyEmail || !notifyEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setNotifyLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/notify-when-in-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: notifyEmail,
          product_id: product.product_id,
          variant_size: currentVariant?.size
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setNotifySubscribed(true);
        toast.success(data.message || "You'll be notified when this item is back in stock!");
      } else {
        toast.error(data.detail || 'Failed to subscribe');
      }
    } catch (error) {
      toast.error('Failed to subscribe for notifications');
    } finally {
      setNotifyLoading(false);
    }
  };

  // Get starting price for display
  const getStartingPrice = () => {
    if (!product?.variants) return null;
    const prices = product.variants.map(v => v.price);
    return Math.min(...prices);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <MoleculeLoader size={96} label="Loading peptide details…" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Exclusive Product</h2>
          <p className="text-slate-600 mb-6">
            This product is only available to select customer tiers. Please contact us or sign in with an eligible account to purchase.
          </p>
          <div className="space-y-3">
            <Link to="/auth">
              <Button className="w-full bg-teal-700 hover:bg-teal-800">
                Sign In to Access
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Products
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <FlaskConical className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading font-semibold text-slate-900 mb-2">Product not found</h2>
          <Link to="/products">
            <Button variant="outline">Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {product && (
        <SEO 
          title={product.name}
          description={product.description}
          keywords={`${product.name}, research peptide, ${product.purity} purity, laboratory peptide`}
          type="product"
          product={product}
        />
      )}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Breadcrumb */}
        <Link 
          to="/products" 
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-teal-700 mb-8"
          data-testid="back-to-products"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="bg-white rounded-2xl border border-slate-100 p-8">
            <div className="aspect-square">
              <img 
                src={getImageUrl(product.image_url)} 
                alt={product.name}
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Product Info */}
          <div>
            {/* Research Disclaimer */}
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 mb-4">
              <Shield className="h-3 w-3 mr-1" />
              For Research Purposes Only
            </Badge>

            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4" data-testid="product-name">
              {product.name}
            </h1>

            <div className="flex items-center gap-4 mb-6">
              <span className="text-sm text-teal-700 font-medium">{product.purity} Purity</span>
            </div>

            <div className="text-slate-600 leading-relaxed mb-6 space-y-3" data-testid="product-description">
              {product.description.split(/\r?\n/).map((line, i) => {
                const trimmed = line.trim();
                if (!trimmed) return null;
                // Section divider
                if (/^[⸻─—]{1,}$/.test(trimmed)) {
                  return <hr key={i} className="border-slate-200 my-3" />;
                }
                // Bullet point
                if (/^[•\-–]\s/.test(trimmed)) {
                  return (
                    <div key={i} className="flex items-start gap-2 pl-2">
                      <span className="text-teal-600 mt-1.5 text-xs">●</span>
                      <span>{trimmed.replace(/^[•\-–]\s*/, '')}</span>
                    </div>
                  );
                }
                // Header-like line (short, starts with capital, no trailing period)
                if (trimmed.length < 80 && /^[A-Z]/.test(trimmed) && !trimmed.endsWith('.') && !trimmed.endsWith(',') && !trimmed.startsWith('⚠')) {
                  return <p key={i} className="font-semibold text-slate-800 pt-1">{trimmed}</p>;
                }
                // Regular paragraph
                return <p key={i}>{trimmed}</p>;
              })}
            </div>

            {/* Size/Variant Selection */}
            {hasVariants && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-3">Select Size</label>
                <div className="flex flex-wrap gap-3">
                  {product.variants.map((variant, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedVariant(index)}
                      disabled={!variant.in_stock}
                      className={`relative px-4 py-3 rounded-xl border-2 transition-all ${
                        selectedVariant === index
                          ? 'border-teal-700 bg-teal-50'
                          : variant.in_stock
                            ? 'border-slate-200 hover:border-teal-300'
                            : 'border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed'
                      }`}
                      data-testid={`variant-${index}`}
                    >
                      <div className="text-left">
                        <div className={`font-semibold ${selectedVariant === index ? 'text-teal-700' : 'text-slate-900'}`}>
                          {variant.size}
                        </div>
                        {variant.is_on_sale ? (
                          <div className="flex items-baseline gap-1.5 text-sm">
                            <span className="text-rose-600 font-semibold">
                              ${Number(variant.sale_price).toFixed(2)}
                            </span>
                            <span className="text-slate-400 line-through text-xs">
                              ${Number(variant.original_price).toFixed(2)}
                            </span>
                          </div>
                        ) : (
                          <div className={`text-sm ${selectedVariant === index ? 'text-teal-600' : 'text-slate-500'}`}>
                            ${variant.price.toFixed(2)}
                          </div>
                        )}
                      </div>
                      {variant.is_on_sale && (
                        <div className="absolute -top-2 -left-2">
                          <Badge className="bg-rose-600 text-white text-[10px] shadow-sm" data-testid={`variant-${index}-sale-badge`}>
                            {variant.sale_label || 'Sale'}
                          </Badge>
                        </div>
                      )}
                      {!variant.in_stock && (
                        <div className="absolute -top-2 -right-2">
                          <Badge className="bg-red-100 text-red-700 text-[10px]">Out of Stock</Badge>
                        </div>
                      )}
                      {selectedVariant === index && variant.in_stock && (
                        <div className="absolute -top-1 -right-1">
                          <CheckCircle2 className="h-5 w-5 text-teal-700 bg-white rounded-full" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Price */}
            <div className="mb-6">
              {currentVariant?.is_on_sale ? (
                <div className="space-y-1">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-heading text-4xl font-bold text-rose-600" data-testid="product-price">
                      ${Number(currentVariant.sale_price).toFixed(2)}
                    </span>
                    <span className="text-2xl text-slate-400 line-through" data-testid="product-original-price">
                      ${Number(currentVariant.original_price).toFixed(2)}
                    </span>
                    {currentVariant && (
                      <span className="text-slate-500">/ {currentVariant.size}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-rose-600 text-white" data-testid="product-sale-badge">
                      {currentVariant.sale_label || 'On Sale'}
                    </Badge>
                    {currentVariant.savings > 0 && (
                      <span className="text-sm text-rose-700 font-medium">
                        You save ${Number(currentVariant.savings).toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-4xl font-bold text-teal-700" data-testid="product-price">
                    ${currentVariant?.price?.toFixed(2)}
                  </span>
                  {currentVariant && (
                    <span className="text-slate-500">/ {currentVariant.size}</span>
                  )}
                </div>
              )}
              {currentVariant?.in_stock ? (
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-green-50 text-green-700 border-green-200">In Stock</Badge>
                  {currentVariant.stock_quantity > 0 && (
                    <span className="text-xs text-slate-400">({currentVariant.stock_quantity} available)</span>
                  )}
                </div>
              ) : (
                <Badge className="bg-red-50 text-red-700 border-red-200 mt-2">Out of Stock</Badge>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-2">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="quantity-minus"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="font-medium w-8 text-center" data-testid="quantity-value">{quantity}</span>
                <button 
                  onClick={() => {
                    const maxStock = currentVariant?.stock_quantity;
                    if (currentVariant?.in_stock && typeof maxStock === 'number' && maxStock > 0 && quantity >= maxStock) {
                      toast.error(`Only ${maxStock} in stock.`);
                      return;
                    }
                    setQuantity(quantity + 1);
                  }}
                  className={`${currentVariant?.in_stock && currentVariant?.stock_quantity > 0 && quantity >= currentVariant.stock_quantity ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="quantity-plus"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              <Button 
                size="lg" 
                disabled={!variantInStock && !canPreorder}
                className={`flex-1 h-12 text-white ${variantInStock ? 'bg-teal-700 hover:bg-teal-800' : canPreorder ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-400 cursor-not-allowed'}`}
                onClick={() => {
                  if (variantInStock) { handleAddToCart(false); }
                  else if (canPreorder) { handleAddToCart(true); }
                }}
                data-testid={variantInStock ? "add-to-cart" : canPreorder ? "pre-order-btn" : "out-of-stock-btn"}
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                {variantInStock ? 'Add to Cart' : canPreorder ? 'Pre-Order' : 'Out of Stock'}
              </Button>
              {user && (
                <Button
                  size="lg"
                  variant="outline"
                  className={`h-12 px-4 ${isInWishlist(product.product_id) ? 'border-rose-300 bg-rose-50 text-rose-600 hover:bg-rose-100' : 'border-slate-200 text-slate-500 hover:text-rose-500 hover:border-rose-200'}`}
                  onClick={() => { toggleWishlist(product.product_id); toast.success(isInWishlist(product.product_id) ? 'Removed from wishlist' : 'Added to wishlist'); }}
                  data-testid="wishlist-toggle-detail"
                >
                  <Heart className={`h-5 w-5 ${isInWishlist(product.product_id) ? 'fill-rose-500' : ''}`} />
                </Button>
              )}
            </div>

            {/* Pre-Order Disclaimer */}
            {canPreorder && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="preorder-disclaimer">
                <p className="text-sm text-amber-800 font-medium">Pre-Order Item</p>
                <p className="text-xs text-amber-600 mt-1">
                  Longer shipping times may apply due to availability. Usually 2 weeks.
                </p>
              </div>
            )}

            {/* Notify When Back In Stock */}
            {!variantInStock && (
              <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="h-5 w-5 text-amber-600" />
                  <span className="font-medium text-amber-800">Get notified when back in stock</span>
                </div>
                {notifySubscribed ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>You'll be notified when this item is available!</span>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        className="pl-10"
                        data-testid="notify-email"
                      />
                    </div>
                    <Button
                      onClick={handleNotifyMe}
                      disabled={notifyLoading}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="notify-btn"
                    >
                      {notifyLoading ? 'Subscribing...' : 'Notify Me'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Product Features */}
            <Card className="bg-slate-50 border-slate-100">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-slate-900 mb-4">Product Features</h3>
                <ul className="space-y-3">
                  {[
                    'Third-party lab tested',
                    `Purity verified at ${product.purity}`,
                    'Full Certificate of Analysis available',
                    'Synthesized in USA',
                    'Research-grade quality'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3 text-sm text-slate-600">
                      <CheckCircle2 className="h-4 w-4 text-teal-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* COA Link */}
            <Link to="/coas" className="mt-4 inline-flex items-center gap-2 text-sm text-teal-700 hover:underline">
              <FileText className="h-4 w-4" />
              View Certificates of Analysis
            </Link>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((related) => {
                const startingPrice = related.variants 
                  ? Math.min(...related.variants.map(v => v.price))
                  : related.price;
                return (
                  <Link key={related.product_id} to={`/products/${related.slug}`}>
                    <Card className="group h-full bg-white border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300">
                      <div className="aspect-square p-4 bg-slate-50">
                        <img 
                          src={getImageUrl(related.image_url)} 
                          alt={related.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-heading font-semibold text-slate-900 mb-1 group-hover:text-teal-700 transition-colors">
                          {related.name}
                        </h3>
                        <p className="text-sm text-slate-500 mb-2">
                          {related.variants ? `${related.variants.length} sizes available` : related.vial_size}
                        </p>
                        <span className="font-heading font-bold text-teal-700">
                          {related.variants ? `From $${startingPrice.toFixed(2)}` : `$${related.price?.toFixed(2)}`}
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Compliance Notice */}
        <div className="mt-16 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <Shield className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-heading font-semibold text-amber-800 mb-2">Research Use Only</h3>
              <p className="text-sm text-amber-700">
                This product is intended for laboratory research purposes only. It is not intended for 
                human or animal consumption. Not a drug, food, or cosmetic. Not intended to diagnose, 
                treat, cure, or prevent any disease.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
